import { useEffect, useState, useContext } from "react";
import { GetSearch, GetSearchFuzzy } from "../../services/searchServices";
import "./searchResults.css";
import { useLocation, useHistory } from "react-router-dom";
import i18n from "i18next";
import { MobXProviderContext } from "mobx-react";
import LoadingSpinner from "../../utilities/loadingSpinner";

function getActiveFilters(params) {
  const labels = {
    // legacy / existing
    q: "Trefwoord",
    titel: "Titel",
    auteur: "Auteur",
    trefwoord: "Trefwoord",
    categorie: "Categorie",
    media: "Media",
    prijs: "Prijs",
    isbn: "ISBN",

    // new (from your SearchInput)
    keyword: "Trefwoord",
    title: "Titel",
    author: "Auteur",
  };

  return Object.entries(params)
    .filter(([key, value]) => value !== "" && labels[key.toLowerCase()])
    .map(([key, value]) => ({
      label: labels[key.toLowerCase()],
      value,
    }));
}

function getStockFeedback(book) {
  const stockCode = Number(book.stock_code);
    const isEigenUitgave =
    book.sb_edition?.toLowerCase().includes("eigen uitgave") ||
    book.tags?.toLowerCase().includes("eigen uitgave");
  let value = "";

  switch (stockCode) {
    case 21:
    case 23:
    case 30:
      if (isEigenUitgave) {
        value = "Op werkdagen voor 16:00 besteld, dezelfde dag nog verzonden.";
      } else {
        value = "Levertijd: 1-3 werkdagen";
      }
      break;

    case 22:
      value = "Levertijd: 7-9 werkdagen";
      break;

    case 10: {
      value = "Nog niet verschenen";
      const supplyDate = book?.supply_date?.date || book?.supply_date;
      if (supplyDate) {
        value += `, verwacht op ${new Date(supplyDate).toLocaleDateString(
          "nl-NL",
          { day: "2-digit", month: "long", year: "numeric" }
        )}`;
      }
      break;
    }

    case 31: {
      value = "Nog niet verschenen";
      const releaseDate = book?.release_date?.date || book?.release_date;
      if (releaseDate) {
        value += `, verwacht op ${new Date(releaseDate).toLocaleDateString(
          "nl-NL",
          { day: "2-digit", month: "long", year: "numeric" }
        )}`;
      }
      break;
    }

    case 32: {
      value = "In herdruk";
      const supplyDate = book?.supply_date?.date || book?.supply_date;
      if (supplyDate) {
        value += `, verwacht op ${new Date(supplyDate).toLocaleDateString(
          "nl-NL",
          { day: "2-digit", month: "long", year: "numeric" }
        )}`;
      }
      break;
    }

    default:
      value = "Uitverkocht";
  }

  return value;
}

function getAuthorSlug(book) {
  return [book.author_firstname, book.author_prefix, book.author_lastname]
    .filter(Boolean)
    .join(" ");
}

function isIsbn13(value) {
  return /^\d{13}$/.test(value);
}

function getSearchTerm(params) {
  // supports both legacy and new query keys
  return (
    params.q ||
    params.keyword ||
    params.trefwoord ||
    params.titel ||
    params.title ||
    params.auteur ||
    params.author ||
    params.isbn ||
    params.ISBN ||
    params.categorie ||
    ""
  );
}

export default function SearchResults() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [loadingMessage, setLoadingMessage] = useState(null);

  const location = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(location.search);
  const params = Object.fromEntries(queryParams.entries());

  const searchTerm = getSearchTerm(params);
  const hasSearchParams = Object.keys(params).length > 0;
  const activeFilters = getActiveFilters(params);

  const { stores } = useContext(MobXProviderContext);

  const {
    storeGeneral,
    storeUi,
    storeUser,
    storeOrder,
    storeWishlist,
    storeStock,
    storeSearch,
  } = stores;

  function gotoAuthor(e, book) {
    e.preventDefault();
    e.stopPropagation();

    const slug = getAuthorSlug(book);
    history.push(`/author/${slug}`);
  }

  function handleAddToCart(e, book) {
    e.preventDefault();
    e.stopPropagation();

    const value = 1;
    const userId = storeUser.user_data.is_loggedin
      ? storeUser.user_data.user_id
      : 0;

    storeOrder.addToCart(storeGeneral.shop, userId, book, "add", value);
    storeUi.openDialog();
  }

  useEffect(() => {
    if (!hasSearchParams) return;

    const isbn = params.ISBN || params.isbn;
    if (isbn && isIsbn13(isbn)) {
      history.push(`/book/${isbn}`);
      return;
    }

    if (storeSearch.cachedQuery === location.search && storeSearch.cachedResults.length > 0) {
      setResults(storeSearch.cachedResults);
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setResults([]);

      try {
        const data = await GetSearch(params);
        if (cancelled) return;

        if (
          params.title &&
          Array.isArray(data) &&
          data.length === 1 &&
          data[0]?.sku
        ) {
          history.replace(`/book/${data[0].sku}`);
          return;
        }

        if (data && data.length > 0) {
          storeSearch.setCachedSearch(location.search, data);
          setResults(data);
          return;
        }

        const fuzzyData = await GetSearchFuzzy(params);
        if (cancelled) return;

        const finalResults = fuzzyData || [];
        storeSearch.setCachedSearch(location.search, finalResults);
        setResults(finalResults);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [location.search]);

  useEffect(() => {
    if (!loading) {
      setLoadingMessage(null);
      return;
    }

    const t1 = setTimeout(() => {
      setLoadingMessage("Bezig met laden?");
    }, 800);

    const t2 = setTimeout(() => {
      setLoadingMessage("Boeken worden geindexeerd en naar voren gehaald?");
    }, 2000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [loading]);

  if (!hasSearchParams) {
    return (
      <div className="container page-search-results">
        <div className="search-initial-state">
          <h2>Waar ben je naar op zoek?</h2>
          <p>Zoek op titel, auteur, ISBN of onderwerp.</p>

          <div className="search-initial-actions">
            <a className="button button--primary" href="/">
              Bekijk bestsellers
            </a>
          </div>

          <div className="search-top-results">
            <h3>Populaire boeken</h3>
            <ul className="search-list search-list--compact">
              {storeGeneral.top_books?.slice(0, 10).map((book) => (
                <li
                  key={book.id}
                  className="search-list__item"
                  onClick={() => history.push(`/book/${book.sku}`)}
                >
                  <span className="search-item__data--title">{book.title}</span>
                  <span className="search-item__data--author">
                    {formatAuthor(book)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  function formatAuthor(book) {
    return [book.author_firstname, book.author_prefix, book.author_lastname]
      .filter(Boolean)
      .join(" ");
  }

  function getCoverUrl(book) {
    if (!book?.sku) {
      return ["/placeholder-cover.webp"];
    }

    const base = "https://www.succesboeken.nl/api/public/images";

    return [
      `${base}/CoversVKg-webp/${book.sku}.webp`,
      `${base}/CoversVKg-all/${book.sku}.jpg`,
      `${base}/CoversVKg/${book.sku}.jpg`,
      `${base}/CoversVKg-all/${book.sku}.png`,
      `${base}/Covers/${book.sku}.jpg`,
      `${base}/img-not-available.png`,
    ];
  }

  const langMap = {
    N: "Nederlands",
    E: "Engels",
    DE: "Duits",
  };

  function gotoBook(book) {
    history.push(`/book/${book.sku}`);
  }

  function FallbackImage({ sources, alt }) {
    const [index, setIndex] = useState(0);

    return (
      <img
        src={sources[index]}
        alt={alt}
        width="80"
        height="120"
        loading="lazy"
        decoding="async"
        onError={() => {
          if (index < sources.length - 1) {
            setIndex(index + 1);
          }
        }}
      />
    );
  }

  return (
  <div className="container page-search-results">
    {!loading && results.length === 0 && (
      <div className="search-no-results">
        <p>
          Er kan niks gevonden worden met de woorden{" "}
          <strong>"{searchTerm}"</strong>
        </p>
      </div>
    )}
    {activeFilters.length > 0 && (
      <div className="active-filters">
        <h3>Zoekopdracht</h3>
        <ul>
          {activeFilters.map((f) => (
            <li key={`${f.label}-${f.value}`}>
              <strong>{f.label}:</strong> {f.value}
            </li>
          ))}
        </ul>
      </div>
    )}
    {loading && (
      <div className="loading-state">
        <LoadingSpinner />
        {/* {loadingMessage && <div className="loading-text">{loadingMessage}</div>} */}
      </div>
    )}
    {!loading && results.length > 0 && (
      <ul className="search-list">
        {results.map((book) => {
          // Nieuwe helper voor extra tags (op basis van getStockFeedback)
          const feedback = getStockFeedback(book);
          const isEigenUitgave =
            book.sb_edition?.toLowerCase().includes("eigen uitgave") ||
            book.tags?.toLowerCase().includes("eigen uitgave");

          const isDefinitiefUitverkocht = !storeStock.checkStockAvailability("order", book) &&
            !feedback.includes("Binnenkort") &&
            !feedback.includes("verwacht op") &&
            !feedback.includes("In herdruk");

          const isBinnenkortBeschikbaar = feedback.includes("Binnenkort");
          const isInHerdruk = feedback.includes("In herdruk");
          const isNogNietVerschenen = feedback.includes("Nog niet verschenen");

          return (
            <li
              key={book.id}
              className="search-list__item"
              onClick={() => gotoBook(book)}
            >
              <div className="search-item">
                <div className="search-item__img">
                  {/* Tags */}
                  {isEigenUitgave && (
                    <span className="cover__tag cover__tag--eigen">Eigen uitgave</span>
                  )}

                  {isBinnenkortBeschikbaar && (
                    <span className="cover__tag cover__tag--soon">Binnenkort beschikbaar</span>
                  )}

                  {isInHerdruk && (
                    <span className="cover__tag cover__tag--reprint">In herdruk</span>
                  )}

                  {isNogNietVerschenen && (
                    <span className="cover__tag cover__tag--preorder">Pre-order</span>
                  )}

                  {isDefinitiefUitverkocht && (
                    <span className="cover__tag cover__tag--uitverkocht">Uitverkocht</span>
                  )}

                  {/* Wrapper voor grayscale ? alleen bij definitief uitverkocht */}
                  <div
                    className={`cover-wrapper ${
                      isDefinitiefUitverkocht ? "grayscale" : ""
                    }`}
                  >
                    <FallbackImage
                      sources={getCoverUrl(book)}
                      alt={book.title}
                      loading="lazy"
                    />
                  </div>
                </div>

                <div className="search-item__data">
                  <div className="search-item__main">
                    <span className="search-item__data--title">{book.title}</span>

                    {formatAuthor(book) && (
                      <a
                        className="search-item__data--author"
                        onClick={(e) => gotoAuthor(e, book)}
                      >
                        {formatAuthor(book)}
                      </a>
                    )}
                    <div className="search-item__specs">
                      <ul>
                        <li>{langMap[book.lang] ?? book.lang}</li>
                        <li>{book.version}</li>
                        <li>ISBN: {book.sku}</li>
                      </ul>
                    </div>
                  </div>

                  <div className="search-item__cta">
                    <div className="book-price">
                      &euro; {book.price_incl ? book.price_incl.toFixed(2) : ""}
                    </div>
                    <div className="delivery">{feedback}</div>

                    <div className="add-shoppingcart">
                      {storeStock.checkStockAvailability("order", book) ? (
                        <a
                          className="button button--primary button--cart"
                          onClick={(e) => handleAddToCart(e, book)}
                        >
                          {i18n.t("button.shoppingcart")}
                        </a>
                      ) : (
                        <a className="button button--primary button--cart --disabled">
                          {i18n.t("button.shoppingcart")}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    )}
  </div>
);
}
